import { ColumnsType, Group } from '@/components/_common';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { InvoiceSituation } from '@/core/domain/invoice/invoices/invoices.enums';
import { Popover } from 'antd';
import { ReactElement } from 'react';
import ActionIcon from '../ActionIcon';
import { useReportGenerate } from '@/hooks';
import {
  DocTypeEnum,
  DownloadXmlInvoiceRequest,
  DownloadXmlInvoiceResponseMessage,
  InvoiceColumns,
} from '@/core/domain/invoice/invoice/Invoice.types';
import { useInvoiceDocumentDownload } from '@/core/domain/invoice/invoice/invoice.hooks';
import arrayBufferParse from '@/utils/functions/arrayBufferParse';
import { Eye } from 'react-feather';
import useInvoiceFunctions from '@/core/domain/invoiceTransmissionHub/Invoice/Invoice.functions';

const iconFileStyle = {
  fontWeight: 900,
  fontSize: 15,
  letterSpacing: '-1px',
};

function useInvoiceFilesColumn() {
  const { handleInvoicePdf, handleInvoiceXml } = useInvoiceFunctions();

  const report = useReportGenerate();
  const downloadInvoiceDoc = useInvoiceDocumentDownload();

  const handleRequestFile = async (
    obj: DownloadXmlInvoiceRequest,
    invoice: InvoiceColumns,
    docType: DocTypeEnum,
  ) => {
    const invoiceType = invoice?.invoiceType;
    const invoiceId = invoice?.id;

    if (invoice?.isInternalApiIssued && docType === DocTypeEnum.xml) {
      return await handleInvoiceXml(invoiceType, invoiceId);
    }

    if (invoice?.isInternalApiIssued && docType === DocTypeEnum.pdf) {
      return await handleInvoicePdf(invoiceType, invoiceId);
    }

    if (invoice?.isInternalApiIssued && docType === DocTypeEnum.view) {
      return await handleInvoicePdf(invoiceType, invoiceId);
    }

    return await downloadInvoiceDoc.mutateAsync(obj);
  };

  const handleDownloadDoc = async (
    invoice: InvoiceColumns,
    docType: DocTypeEnum,
  ) => {
    try {
      const obj: DownloadXmlInvoiceRequest = {
        invoiceId: Number(invoice.id),
        docType: !docType ? DocTypeEnum.xml : DocTypeEnum.pdf,
      };

      const result = await handleRequestFile(obj, invoice, docType);
      const response =
        arrayBufferParse<DownloadXmlInvoiceResponseMessage>(result);

      if (!response?.message) {
        if (docType === DocTypeEnum.xml) {
          report.downloadFile({
            result,
            fileName: `${invoice.invoiceType}-${invoice?.number}.xml`,
          });
        } else if (docType === DocTypeEnum.pdf) {
          report.downloadFile({
            result,
            fileName: `${invoice.invoiceType}-${invoice?.number}.pdf`,
          });
        } else {
          report.openFile({
            result,
            fileName: `${invoice.invoiceType}-${invoice?.number}.pdf`,
            fileType: 'application/pdf',
          });
        }
      }
    } catch {}
  };

  const column: ColumnsType<InvoiceColumns> = [
    {
      title: 'Arquivo da nota',
      width: 150,
      align: 'right',
      render: (_, row) => {
        const enebledIcon =
          row.situation === InvoiceSituation.AUTHORIZED ||
          row.situation === InvoiceSituation.CANCELED;

        const renderIcon = (icon: ReactElement) => (
          <Popover content={!enebledIcon ? 'Sem arquivo' : undefined}>
            <div>{icon}</div>
          </Popover>
        );

        return (
          <Group position="right" align="center" gap={10}>
            {renderIcon(
              <ActionIcon
                onClick={() => handleDownloadDoc(row, DocTypeEnum.xml)}
                disabled={
                  !hasPermissions([Authorities.FISCAL_INVOICE_DOWNLOAD_XML]) ||
                  !enebledIcon ||
                  downloadInvoiceDoc.isLoading
                }
              >
                <span style={iconFileStyle}>XML</span>
              </ActionIcon>,
            )}

            {renderIcon(
              <ActionIcon
                onClick={() => handleDownloadDoc(row, DocTypeEnum.pdf)}
                disabled={!enebledIcon || downloadInvoiceDoc.isLoading}
              >
                <span style={iconFileStyle}>PDF</span>
              </ActionIcon>,
            )}

            {renderIcon(
              <ActionIcon
                onClick={() => handleDownloadDoc(row, DocTypeEnum.view)}
                disabled={!enebledIcon || downloadInvoiceDoc.isLoading}
              >
                <Eye size={20} />
              </ActionIcon>,
            )}
          </Group>
        );
      },
    },
  ];

  return column;
}

export default useInvoiceFilesColumn;
